// App.js
import * as THREE from 'three';
import React, { Suspense, useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Canvas, useFrame, extend, useThree, useLoader } from '@react-three/fiber';
import { Html, Preload, OrbitControls, AdaptiveDpr, PerformanceMonitor, useCursor, Billboard, Environment, Text } from '@react-three/drei';
import { geometry } from 'maath';
import { PreloadProvider, usePreload, ProgressProvider } from './components/PreloadContext';
import { LoadingScreen } from "./components/LoadingScreen";
import { StartingScreen } from "./components/StartingScreen";
import config from './config/rooms.json';
import exitIcon from './assets/marker-icon.png';
import './styles.css'
import { Frame } from './components/Frame';
import { TextureLoader } from 'three';

extend(geometry);

const DEFAULT_CAMERA_POSITION = [0, 0, 0.1];

// Add RoomSelectorMarker component before the App component
function RoomSelectorMarker({ position, availableRooms, onRoomSelect, isOpen, setIsOpen, onMarkerClick, onExitToHome }) {
  const [hovered, hover] = useState(false);
  useCursor(hovered, 'pointer');

  return (
    <Billboard
      position={position}
      follow={true}
      lockX={false}
      lockY={true}
      lockZ={true}
    >
      <group rotation={[0, 0, 0]}>
        <Html
          transform
          className="roomSelector_container"
          position={[0, 0, 2]}
        >
          <div 
            className="roomSelector"
            onMouseEnter={() => hover(true)}
            onMouseLeave={() => hover(false)}
            onClick={() => {
              onMarkerClick();
              setIsOpen(!isOpen);
            }}
          >
            <div className="roomMarker_title" style={{opacity: hovered ? 1 : 0}}>
              Change Room
            </div>
            <img 
              src={exitIcon} 
              alt="Room marker"
              className="roomSelector_icon"
            />
            
            {isOpen && (
              <div className="roomSelector_dropdown">
                {availableRooms.map((room, index) => (
                  <div
                    key={index}
                    className="roomSelector_option"
                    onClick={(e) => {
                      e.stopPropagation();
                      onRoomSelect(room.index);
                      setIsOpen(false);
                    }}
                  >
                    {room.name}
                  </div>
                ))}
                <div className="roomSelector_divider" />
                <div
                  className="roomSelector_option roomSelector_exit"
                  onClick={(e) => {
                    e.stopPropagation();
                    onExitToHome();
                  }}
                >
                  HOME
                </div>
              </div>
            )}
          </div>
        </Html>
      </group>
    </Billboard>
  );
}

function App() {
  const [start, setStart] = useState(false);
  const [dpr, setDpr] = useState(1.5);
  const [currentZoneIndex, setCurrentZoneIndex] = useState(null);
  const [currentRoomIndex, setCurrentRoomIndex] = useState(0);
  const [isRoomLoading, setIsRoomLoading] = useState(true);
  const [hasStarted, setHasStarted] = useState(false);
  const [previousRoom, setPreviousRoom] = useState(null);
  const [orbitControls, setOrbitControls] = useState({
    minPolarAngle: 0,
    maxPolarAngle: Math.PI / 2
  });
  const orbitControlsRef = useRef();

  // Define default camera positions for different spaces
  const defaultCameraPositions = {
    home: {
      position: [0, 0, 0.1],
      rotation: [0, 0, 0]
    },
    zones: {
      "Interior Design": {
        position: [0, 0, 0.1],
        rotation: [0, 0, 0]
      },
      "Architecture": {
        position: [0, 0.2, 0.1],
        rotation: [0, 0, 0]
      },
      // Add more zones as needed
    }
  };

  // Get current room if we're in a zone
  const currentRoom = currentZoneIndex !== null 
    ? config.zones[currentZoneIndex].rooms[currentRoomIndex]
    : null;

  const resetCamera = useCallback(() => {
    if (orbitControlsRef.current) {
      // Always use the same position
      orbitControlsRef.current.object.position.set(...DEFAULT_CAMERA_POSITION);
      
      // Get rotation from current view if in a room
      let rotation = [0, 0, 0];
      if (currentRoom?.views?.[currentRoomIndex]) {
        rotation = currentRoom.views[currentRoomIndex].cameraRotation || rotation;
      }
      
      orbitControlsRef.current.object.rotation.set(...rotation);
      orbitControlsRef.current.reset();
      orbitControlsRef.current.update();
    }
  }, [currentRoom, currentRoomIndex]);

  const handleZoneEnter = (zoneIndex) => {
    setIsRoomLoading(true);
    setCurrentZoneIndex(zoneIndex);
    setCurrentRoomIndex(0);
    resetCamera();
  };

  const handleExitToHome = () => {
    setIsRoomLoading(true);
    setPreviousRoom(null);
    setCurrentZoneIndex(null);
    setCurrentRoomIndex(0);
    resetCamera();
  };

  const handleRoomChange = (newRoomIndex) => {
    if (currentRoom) {
      setIsRoomLoading(true);
      setPreviousRoom(currentRoom);
      setCurrentRoomIndex(newRoomIndex);
      resetCamera();
    }
  };

  const handleStartClick = () => {
    setHasStarted(true);
    setStart(true);
    setIsRoomLoading(false);
  };

  const handleLoadComplete = () => {
    setPreviousRoom(null);
    setIsRoomLoading(false);
  };

  // Update orbit controls when changing between home and scene
  useEffect(() => {
    if (currentZoneIndex === null) {
      // Home space controls
      setOrbitControls({
        minPolarAngle: Math.PI / 2,
        maxPolarAngle: Math.PI / 2
      });
    } else {
      // Scene controls
      setOrbitControls({
        minPolarAngle: 0,
        maxPolarAngle: Math.PI
      });
    }
  }, [currentZoneIndex]);

    // Calculate frame positions in a circle
    const framePositions = useMemo(() => {
      const radius = 2; // Distance from center
      const frames = [];
      const totalFrames = config.zones.length;
      // Add initial offset to make first frame (Interior Design) face forward
      const startAngle = Math.PI; // Start at -90 degrees (front center)
      
      for(let i = 0; i < totalFrames; i++) {
        const angle = startAngle + (i / totalFrames) * Math.PI * 2;
        frames.push({
          position: [
            Math.sin(angle) * radius,
            0, // Keep Y position constant
            Math.cos(angle) * radius
          ],
          rotation: [0, angle - Math.PI, 0]
        });
      }
      return frames;
    }, [config.zones.length]);

  return (
    <ProgressProvider>
      <PreloadProvider 
        room={currentRoom} 
        onLoadComplete={handleLoadComplete}
      >
        {!hasStarted ? (
          <StartingScreen 
            started={true}
            onStarted={handleStartClick}
          />
        ) : (
          <>
            {isRoomLoading && (
              <LoadingScreen 
                started={true}
                onStarted={() => {}}
              />
            )}
            
            <Canvas 
              shadows 
              dpr={[1, 2]} 
              flat 
              gl={{ antialias: true, preserveDrawingBuffer: true }} 
              camera={{ fov: 75, position: [0, 0, 0.1], near: 0.1, far: 1000 }}
            >
              <Suspense fallback={null}>
                <AdaptiveDpr pixelated />
                <PerformanceMonitor onIncline={() => setDpr(2)} onDecline={() => setDpr(1)} />
                
                <OrbitControls 
                  ref={orbitControlsRef}
                  enablePan={false} 
                  enableZoom={false}
                  enableDamping={true}
                  minPolarAngle={orbitControls.minPolarAngle}
                  maxPolarAngle={orbitControls.maxPolarAngle}
                />

                {!isRoomLoading && currentZoneIndex === null && (
                  <HomeSpace 
                    zones={config.zones}
                    onZoneEnter={handleZoneEnter}
                    framePositions={framePositions}
                  />
                )}

                {!isRoomLoading && currentZoneIndex !== null && currentRoom && (
                  <Scene 
                    room={currentRoom}
                    zoneIndex={currentZoneIndex}
                    onRoomChange={handleRoomChange}
                    onExitToHome={handleExitToHome}
                  />
                )}
              </Suspense>
            </Canvas>
          </>
        )}
      </PreloadProvider>
    </ProgressProvider>
  );
}

// New HomeSpace component
function HomeSpace({ zones, onZoneEnter, framePositions }) {
  // Move texture loading outside
  const frameTextures = useLoader(TextureLoader, zones.map(zone => {
    if (!zone.frame_img) return null;
    try {
      return require(`./assets/${zone.frame_img}`);
    } catch (error) {
      console.error(`Failed to load frame image: ${zone.frame_img}`, error);
      return null;
    }
  }));

  // Remove scrollSpeed and useEffect with wheel event listener
  const controlsRef = useRef();

  return (
    <>
      <color attach="background" args={['#000000']} />
      
      <OrbitControls
        ref={controlsRef}
        enableZoom={false}
        enablePan={false}
        enableDamping={true}
        dampingFactor={0.05}
        rotateSpeed={0.5}
        minPolarAngle={Math.PI / 2}
        maxPolarAngle={Math.PI / 2}
      />
      
      <Billboard>
        <Text
          color="#a1a1a1"
          font="/wp-content/plugins/virtualspace-demo/build/Futura_medium.ttf"
          fontSize={0.08}
          textAlign={'center'}
          lineHeight={1.2}
          anchorX="center"
          position={[0, 1.1, -2]}
          material-toneMapped={false}
        >
          ← Select a space to explore →
        </Text>
      </Billboard>

      {zones.map((zone, index) => {
        if (zone.rooms.length === 0) return null;
        
        return (
          <Frame
            key={index}
            id={index.toString()}
            name={zone.name}
            position={framePositions[index].position}
            rotation={framePositions[index].rotation}
            texture={frameTextures[index]}
            onEnter={() => onZoneEnter(index)}
          />
        );
      })}
    </>
  );
}

// Modify Scene component to include exit functionality
const Scene = ({ room, zoneIndex, onRoomChange, onExitToHome }) => {
  if (!room) return null;
  
  return (
    <>
      <Portals 
        room={room}
        zoneIndex={zoneIndex}
        currentRoomIndex={config.zones[zoneIndex].rooms.findIndex(r => r.name === room.name)}
        onRoomChange={onRoomChange}
        onExitToHome={onExitToHome}
      />
      <Preload all />
    </>
  );
};

// Update RoomDome component to include animated cone
function RoomDome({ viewPortals, onClick, envMap }) {
  const [hoveredPortal, setHoveredPortal] = useState(null);
  useCursor(hoveredPortal !== null, 'pointer');
  const coneRefs = useRef([]);

  // Animation frame for cone hovering
  // useFrame((state, delta) => {
  //   coneRefs.current.forEach((cone) => {
  //     if (cone) {
  //       // Create subtle floating animation
  //       const hoverOffset = Math.sin(state.clock.elapsedTime * 2) * 0.1;
  //       // Move the cone relative to its base position
  //       const baseY = cone.userData.baseY || 0;
  //       cone.position.y = baseY + hoverOffset;
  //     }
  //   });
  // });

  return (
    <group>
      {/* Environment Sphere */}
      <mesh rotation={[0, 0, 0]} scale={[-1, 1, 1]}>
        <sphereGeometry args={[500, 60, 40]} />
        <meshBasicMaterial map={envMap} side={THREE.BackSide} />
      </mesh>

      {/* Map over all portals */}
      {Array.isArray(viewPortals) && viewPortals.map((portal, index) => {
        const isHovered = hoveredPortal === index;
        const portalRadius = 3.5;
        const coneRadius = portalRadius * 0.5;
        const coneHeight = portalRadius;
        
        return (
          <group key={index}>
            {/* Portal Ring */}
            <mesh
              position={portal.position}
              scale={isHovered ? [1.1, 1.1, 1.1] : [1, 1, 1]}
              rotation={[Math.PI / 2, 0, 0]}
              castShadow={false}
            >
              <ringGeometry args={[portalRadius - 0.5, portalRadius, 50]} />
              <meshBasicMaterial opacity={isHovered ? 0.5 : 0.3} color={'white'} transparent side={THREE.DoubleSide} />
            </mesh>

            {/* Portal Circle */}
            <mesh
              position={[portal.position[0], portal.position[1] - 0.01, portal.position[2]]}
              scale={isHovered ? [1.1, 1.1, 1.1] : [1, 1, 1]}
              rotation={[Math.PI / 2, 0, 0]}
              onClick={() => onClick(portal.linkedViewIndex)}
              castShadow={false}
              onPointerOver={() => setHoveredPortal(index)}
              onPointerOut={() => setHoveredPortal(null)}
            >
              <circleGeometry args={[portalRadius, 50]} />
              <meshBasicMaterial opacity={isHovered ? 0.3 : 0.1} color={'white'} transparent side={THREE.DoubleSide} />
            </mesh>

            {/* Inverse Cone - Fixed positioning */}
            <mesh
              // ref={el => {
              //   if (el) {
              //     coneRefs.current[index] = el;
              //     el.userData.baseY = portal.position[1] + (coneHeight / 2); // Store base Y position
              //   }
              // }}
              position={[
                portal.position[0],
                portal.position[1] + (coneHeight / 2), // Position cone half its height above portal
                portal.position[2]
              ]}
              rotation={[Math.PI, 0, 0]} // Flip the cone upside down
              onClick={() => onClick(portal.linkedViewIndex)}
              castShadow={false}
              onPointerOver={() => setHoveredPortal(index)}
              onPointerOut={() => setHoveredPortal(null)}
            >
              <coneGeometry args={[coneRadius, coneHeight, 32]} />
              <meshBasicMaterial 
                color={'white'} 
                transparent 
                opacity={isHovered ? 0.3 : 0.1} 
                side={THREE.DoubleSide}
                depthWrite={false}
              />
            </mesh>
          </group>
        );
      })}
    </group>
  );
}

// Then update the Portals component to use the new marker
function Portals({ room, zoneIndex, currentRoomIndex, onRoomChange, onExitToHome }) {
  const [which, setWhich] = useState(0);
  const [visibleMarker, setVisibleMarker] = useState(null);
  const [selectedSwatch, setSelectedSwatch] = useState(null);
  const [selectedMeshNames, setSelectedMeshNames] = useState([]);
  const [isRoomSelectorOpen, setIsRoomSelectorOpen] = useState(false);
  const { models, environments, swatches, textures, isLoading } = usePreload();
  const [selectedEnvMap, setSelectedEnvMap] = useState(null);
  const { camera } = useThree();
  const orbitControlsRef = useRef();

  // Add refs to store persistent marker selections
  const persistentSelection = useRef({
    markerName: null,
    swatch: null,
    meshNames: [],
    envID: null  // Add this to track the selected environment
  });

  const viewCameraPositions = {
    "view1": {
      position: [0, 0, 0.1],
      rotation: [0, 0, 0]
    },
    "view2": {
      position: [0, 0.1, 0.1],
      rotation: [0, 0, 0]
    }
    // Add more views as needed
  };

  const resetCamera = useCallback(() => {
    if (orbitControlsRef.current) {
      // Use the fixed position
      camera.position.set(...DEFAULT_CAMERA_POSITION);
      
      // Get rotation from current view
      const currentView = room.views[which];
      const rotation = currentView.cameraRotation || [0, 0, 0];
      
      camera.rotation.set(...rotation);
      orbitControlsRef.current.reset();
      orbitControlsRef.current.update();
    }
  }, [camera, room, which]);

  // Helper function to get corresponding envID for next view
  const getCorrespondingEnvID = (currentEnvID, currentViewIndex, nextViewIndex) => {
    // Get all markers for current view that have environment swatches
    const currentViewMarkers = room.views[currentViewIndex].markers;
    const currentMarker = currentViewMarkers?.find(marker => 
      marker.swatches?.some(swatch => swatch.type === 'environment' && swatch.envID === currentEnvID)
    );

    if (!currentMarker) return room.views[nextViewIndex].defaultEnvMapID;

    // Find which variant this envID corresponds to
    const currentVariantIndex = currentMarker.swatches.findIndex(swatch => 
      swatch.type === 'environment' && swatch.envID === currentEnvID
    );

    // Get the corresponding marker in the next view
    const nextViewMarkers = room.views[nextViewIndex].markers;
    const correspondingMarker = nextViewMarkers?.find(marker => 
      marker.name === currentMarker.name && marker.swatches?.some(swatch => swatch.type === 'environment')
    );

    // If we found the corresponding marker and it has enough swatches, use the matching variant
    if (correspondingMarker?.swatches?.[currentVariantIndex]) {
      return correspondingMarker.swatches[currentVariantIndex].envID;
    }

    // Fallback to default
    return room.views[nextViewIndex].defaultEnvMapID;
  };

  // Reset all state when room changes
  useEffect(() => {
    const loadEnvironment = async () => {
      console.log('=== Room Change Debug ===');
      console.log('New Room:', room?.name);
      console.log('Initial View:', room?.views?.[0]);
      
      setWhich(0);
      setVisibleMarker(null);
      setSelectedMeshNames([]);
      setSelectedSwatch(null);
      setIsRoomSelectorOpen(false);
      
      console.log('Clearing persistent selection');
      persistentSelection.current = {
        markerName: null,
        swatch: null,
        meshNames: [],
        envID: null
      };
      
      // Set initial environment and camera rotation
      if (room?.views?.[0]) {
        // Set environment using defaultEnvMapID directly from room config
        const initialEnvMapID = room.views[0].defaultEnvMapID;
        console.log('Setting initial environment:', initialEnvMapID);
        if (environments?.[initialEnvMapID]) {
          setSelectedEnvMap(environments[initialEnvMapID]);
        }
        
        // Set initial camera rotation
        const initialRotation = room.views[0].cameraRotation || [0, 0, 0];
        camera.rotation.set(...initialRotation);
        if (orbitControlsRef.current) {
          orbitControlsRef.current.reset();
          orbitControlsRef.current.update();
        }
      }
    };

    loadEnvironment();
  }, [room, environments, camera]);

  // Early return if loading or no environment map
  if (isLoading || !selectedEnvMap || !room?.views?.[which]) {
    return null;
  }

  const currentView = room.views[which];
  const modelConfig = currentView?.model1;
  const currentModel = models?.[modelConfig?.ID || 0];

  const handleRoomDomeClick = (linkedViewIndex) => {
    console.log('=== View Change Debug ===');
    console.log('Changing to view index:', linkedViewIndex);
    console.log('Current persistent selection:', persistentSelection.current);
    
    const nextView = linkedViewIndex;
    if (nextView !== undefined) {
      setWhich(nextView);
      
      const nextViewMarkers = room.views[nextView].markers || [];
      const currentMarkerName = persistentSelection.current.markerName;
      
      console.log('Looking for marker:', currentMarkerName);
      console.log('Available markers in next view:', nextViewMarkers.map(m => m.name));
      
      if (currentMarkerName) {
        const correspondingMarkerIndex = nextViewMarkers.findIndex(
          marker => marker.name === currentMarkerName
        );

        console.log('Found corresponding marker index:', correspondingMarkerIndex);

        if (correspondingMarkerIndex !== -1) {
          const correspondingMarker = nextViewMarkers[correspondingMarkerIndex];
          
          if (persistentSelection.current.swatch?.type === 'environment') {
            console.log('Attempting to match environment swatch');
            const currentVariantIndex = correspondingMarker.swatches.findIndex(
              swatch => swatch.color === persistentSelection.current.swatch.color
            );
            
            console.log('Matching variant index:', currentVariantIndex);
            
            if (currentVariantIndex !== -1) {
              const nextSwatch = correspondingMarker.swatches[currentVariantIndex];
              console.log('Applying matched swatch:', nextSwatch);
              setSelectedEnvMap(environments[nextSwatch.envID]);
              setSelectedSwatch(nextSwatch);
              setVisibleMarker(correspondingMarkerIndex);
              persistentSelection.current.envID = nextSwatch.envID;
            }
          }
        }
      } else {
        console.log('No persistent selection, using default environment');
        const defaultEnvID = room.views[nextView].defaultEnvMapID;
        if (environments[defaultEnvID]) {
          setSelectedEnvMap(environments[defaultEnvID]);
        }
      }
    }
  };

  const handleMarkerClick = (markerIndex, meshNames, markerName) => {
    console.log('=== Marker Click Debug ===');
    console.log('Clicked marker:', { markerIndex, markerName, meshNames });
    
    setIsRoomSelectorOpen(false);
    
    if (visibleMarker === markerIndex) {
      // Only hide the billboard, don't clear the selection
      setVisibleMarker(null);
    } else {
      setVisibleMarker(markerIndex);
      setSelectedMeshNames(meshNames);
      
      // Update persistent selection
      persistentSelection.current = {
        ...persistentSelection.current, // Keep existing selection
        markerName: markerName,
        meshNames: meshNames,
      };
      
      console.log('Updated persistent selection:', persistentSelection.current);
    }
  };

  const handleSwatchClick = (swatch) => {
    console.log('=== Swatch Click Debug ===');
    console.log('Selected swatch:', swatch);
    console.log('Current persistent selection:', persistentSelection.current);
    
    if (swatch.type === 'environment') {
      console.log('Applying environment swatch:', swatch.envID);
      setSelectedSwatch(swatch);
      setSelectedEnvMap(environments[swatch.envID]);
      
      persistentSelection.current = {
        ...persistentSelection.current,
        swatch: swatch,
        envID: swatch.envID
      };
      console.log('Updated persistent selection:', persistentSelection.current);
    } else {
      const previousEnvSwatch = selectedSwatch?.type === 'environment' ? selectedSwatch : null;
      console.log('Applying non-environment swatch, preserving environment:', previousEnvSwatch);
      setSelectedSwatch(swatch);
      if (previousEnvSwatch) {
        setSelectedEnvMap(environments[previousEnvSwatch.envID]);
      }
      persistentSelection.current.swatch = swatch;
    }
  };

  const handleRoomMarkerClick = (roomLinkIndex) => {
    setVisibleMarker(null);
    // Only clear selection when actually changing rooms
    if (roomLinkIndex !== undefined) {
      // Clear selection before room change
      setSelectedMeshNames([]);
      persistentSelection.current = {
        markerName: null,
        swatch: null,
        meshNames: [],
        envID: null
      };
      onRoomChange(roomLinkIndex);
    }
  };

  // Create a handler to close product markers
  const closeProductMarkers = () => {
    setVisibleMarker(null); // Only hide the UI
    // Don't clear selectedMeshNames or persistent selection
  };

  // Fix the availableRooms mapping - there was a bug in the comparison
  const availableRooms = config.zones[zoneIndex].rooms
    .filter(r => r.name !== room.name)
    .map(r => ({
      name: r.name,
      // Fix: was comparing against r.name instead of rm.name
      index: config.zones[zoneIndex].rooms.findIndex(rm => rm.name === r.name)
    }));

  // Also handle exit to home
  const handleExit = () => {
    setSelectedEnvMap(null);
    setIsRoomSelectorOpen(false);
    persistentSelection.current = {
      markerName: null,
      swatch: null,
      meshNames: []
    };
    onExitToHome();
  };

  // Add debug logging
  console.log('Current view portals:', currentView.viewPortals);

  // Add the missing handler
  const handleMarkerVisibilityCheck = (angle, isVisible, markerIndex) => {
    if (angle > Math.PI / 4 && isVisible) {
      setVisibleMarker(null); // Only hide the UI
    }
  };

  return (
    <>
      {/* Environment for lighting */}
      <Environment
        map={selectedEnvMap}
        background={false}
        preset={null}
        scene={null}
        environmentIntensity={1.75}
      />

      {/* RoomDome for background */}
      <RoomDome
        viewPortals={currentView.viewPortals || []}
        onClick={handleRoomDomeClick}
        envMap={selectedEnvMap}
      />

      {currentView.roomPortalPos && (
        <RoomSelectorMarker
          position={currentView.roomPortalPos}
          availableRooms={availableRooms}
          onRoomSelect={handleRoomMarkerClick}
          isOpen={isRoomSelectorOpen}
          setIsOpen={setIsRoomSelectorOpen}
          onMarkerClick={closeProductMarkers}
          onExitToHome={handleExit}  // Use the new handler
        />
      )}

      {/* Product Markers */}
      {currentView.markers?.map((marker, index) => (
        <ProductMarker
          key={index}
          {...marker}
          isVisible={visibleMarker === index}
          onClick={() => handleMarkerClick(index, marker.meshNames, marker.name)}
          onSwatchClick={handleSwatchClick}
          onVisibilityCheck={handleMarkerVisibilityCheck}
          selectedMeshNames={selectedMeshNames}
          persistentSelection={persistentSelection.current}
          index={index} // Add index to props
        />
      ))}

      {/* 3D Model */}
      {currentModel && modelConfig && (
        <Model
          model={currentModel}
          position={modelConfig.position}
          rotation={modelConfig.rotation}
          scale={modelConfig.scale}
          selectedSwatch={selectedSwatch}
          meshNames={selectedMeshNames}
        />
      )}
    </>
  );
}

// Simplify Model component since environment is handled globally
function Model({ model, position, rotation, scale, selectedSwatch, meshNames }) {
  useEffect(() => {
    if (!model) return;

    model.scene.traverse((child) => {
      if (child.isMesh && meshNames?.includes(child.name)) {
        if (selectedSwatch?.type === 'color') {
          child.material.color.set(selectedSwatch.color);
          child.material.metalness = selectedSwatch.metallic || 0;
          child.material.roughness = selectedSwatch.roughness || 1;
        }
        child.material.needsUpdate = true;
      }
    });
  }, [model, selectedSwatch, meshNames]);

  if (!model) return null;

  return (
    <primitive
      object={model.scene || model}
      position={position || [0, 0, 0]}
      rotation={rotation || [0, 0, 0]}
      scale={scale || 1}
    />
  );
}

function ProductMarker({ name, description, position, isVisible, onClick, swatches, onSwatchClick, onVisibilityCheck, index }) {
  const [hovered, hover] = useState(false);
  useCursor(hovered, 'pointer');
  const domeRef1 = useRef();
  const domeRef2 = useRef();
  const materialRef1 = useRef();
  const materialRef2 = useRef();
  const [billboardPosition, setBillboardPosition] = useState([0.5, 9, 0]);
  const [pulse, setPulse] = useState(0);

  useFrame((state, delta) => {
    if (!domeRef1.current || !domeRef2.current || !materialRef1.current || !materialRef2.current) return;

    setPulse((pulse) => (pulse + delta * 0.5) % 1);
    const scale = 1 + 0.1 * Math.sin(pulse * Math.PI * 2);
    domeRef1.current.scale.set(scale, scale, scale);
    domeRef2.current.scale.set(scale, scale, scale);

    const opacity = 0.9 - Math.sin(pulse * Math.PI * 2) * 0.7;
    materialRef1.current.opacity = opacity;
    materialRef2.current.opacity = opacity;

    // Check if marker is too far from camera view direction
    const cameraDirection = new THREE.Vector3();
    state.camera.getWorldDirection(cameraDirection);
    const markerDirection = new THREE.Vector3()
      .subVectors(new THREE.Vector3(...position), state.camera.position)
      .normalize();
    const angle = cameraDirection.angleTo(markerDirection);

    // Use the new visibility check handler
    onVisibilityCheck(angle, isVisible, index);

    // Billboard positioning logic
    const markerPosition = new THREE.Vector3(...position);
    const screenPosition = markerPosition.project(state.camera);
    const y = (screenPosition.y * -0.5 + 0.5) * window.innerHeight;
    setBillboardPosition([0.5, y < window.innerHeight / 2 ? -9.6 : 9, 0]);
  });

  // Helper function to get swatch style
  const getSwatchStyle = (swatch) => {
    if (swatch.swatchID !== undefined && swatches?.[swatch.swatchID]) {
      return {
        backgroundImage: `url(${swatches[swatch.swatchID]})`
      };
    } else if (swatch.color) {
      return {
        backgroundColor: swatch.color
      };
    }
    return {}; // Default empty style
  };

  return (
    <group position={position}>
      <Billboard
        follow={true}
      >
        {/* Marker rings */}
        <mesh ref={domeRef1} castShadow={false} onClick={onClick}>
          <ringGeometry args={[1, 1.25, 50]} />
          <meshBasicMaterial ref={materialRef1} color={'white'} transparent side={THREE.DoubleSide} />
        </mesh>
        <mesh ref={domeRef2} castShadow={false}>
          <circleGeometry args={[0.75, 50]} />
          <meshBasicMaterial ref={materialRef2} color={'white'} transparent side={THREE.DoubleSide} />
        </mesh>

        {/* Clickable area */}
        <mesh onClick={onClick} onPointerOver={() => hover(true)} onPointerOut={() => hover(false)}>
          <circleGeometry args={[2, 50]} />
          <meshBasicMaterial opacity={0} transparent side={THREE.DoubleSide} />
        </mesh>

        {/* Billboard content */}
        {isVisible && (
          <group position={billboardPosition}>
            <Html className="billboard" scale={2} transform>
              <div className="billboard_content">
                <div className="billboard_title">{name}</div>
                <div className="billboard_text">{description}</div>
                <div className="billboard_swatches">
                  {swatches?.map((swatch, index) => (
                    <div
                      key={index}
                      className="billboard_swatch"
                      style={getSwatchStyle(swatch)}
                      onClick={() => onSwatchClick(swatch)}
                    />
                  ))}
                </div>
              </div>
            </Html>
          </group>
        )}
      </Billboard>
    </group>
  );
}

export default App;